import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { CalendarClock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAccountSubscriptions, seatState, TONE_CLASSES } from "@/hooks/useAccountSubscriptions";

/** Shows the account whose seat renews first (managed workspaces only). */
export function NextRenewalCard({ workspaceId }: { workspaceId: string }) {
  const { data: seats = [] } = useAccountSubscriptions(workspaceId);
  const first = useMemo(() =>
    seats.filter((s) => s.client_id && s.current_period_end)
      .sort((a, b) => a.current_period_end!.localeCompare(b.current_period_end!))[0], [seats]);
  const { data: name } = useQuery({
    queryKey: ["seat-first-name", workspaceId, first?.client_id],
    enabled: !!first?.client_id,
    queryFn: async () => {
      const { data } = await supabase.from("clients").select("linkedin_name, email").eq("id", first!.client_id!).maybeSingle();
      return data?.linkedin_name || data?.email || "LinkedIn account";
    },
  });
  if (!first) return null;
  const st = seatState(first);
  return (
    <div className={`flex flex-col gap-1 rounded-lg border px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${TONE_CLASSES[st.tone]}`}>
      <div className="flex items-center gap-2 text-sm">
        <CalendarClock className="h-4 w-4 shrink-0" />
        <span className="text-foreground">
          Next renewal: <span className="font-medium">{name || "…"}</span> on{" "}
          <span className="font-medium">{format(new Date(first.current_period_end!), "d MMM yyyy")}</span>
        </span>
      </div>
      <span className="text-sm font-semibold">{st.label}</span>
    </div>
  );
}
